import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  Typography,
} from '@mui/material';
import {
  DATE_FORMAT,
  useDataProvider,
  useLocalization,
} from '@ui-framework/index';
import { FormattedMessage } from 'react-intl';
import { DialogCloseButton } from '@product-framework/components/Dialogs';
import {
  AvailabilitySlotTypeProvider,
  IconLibrary,
  useAvailabilitySlotTypes,
} from '@product-framework/index';
import dayjs from 'dayjs';
import DispatchCalendarHelper from './DispatchCalendarHelper';

function DispatchCalendarModalContent({
  success,
  loading,
  value,
  isRescheduleFlow = false,
  isReAssign = false,
  isAssign = false,
}) {
  const { getSlotByCode } = useAvailabilitySlotTypes();

  const formattedMessageParams = {
    dated: (
      <Typography component="span" sx={{ fontWeight: 500 }}>
        {dayjs(value?.textDate).format(DATE_FORMAT.FRIENDLY_WEEKDAY_DATE)}
      </Typography>
    ),
    timeslot: (
      <FormattedMessage
        id={value?.slot?.code || ''}
        defaultMessage={getSlotByCode({ code: value?.slot?.code })?.name}
      />
    ),
  };

  return (
    <Box>
      {!success && (
        <Box>
          {loading ? (
            <Box textAlign="center">
              <CircularProgress size="30px" />
              <Typography>
                {DispatchCalendarHelper.getSaveInProgressText({
                  isReAssign,
                  isAssign,
                  isRescheduleFlow,
                })}
                ...
              </Typography>
            </Box>
          ) : (
            <Typography variant="body">
              {DispatchCalendarHelper.getSaveDialogQuestion({
                isReAssign,
                isAssign,
                isRescheduleFlow,
                formattedMessageParams,
              })}
            </Typography>
          )}
        </Box>
      )}
      {success && (
        <Box sx={{ mt: 2 }} textAlign="center">
          <Typography>
            {DispatchCalendarHelper.getSaveSuccessDialogMessage({
              isReAssign,
              isAssign,
              isRescheduleFlow,
            })}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function DispatchCalendarModal({
  open,
  returnToJobPage,
  onClose,
  value,
  jobInfo,
  setErrors,
  isRescheduleFlow = false,
  preferredServiceProviderId = null,
  isReAssign = false,
  isAssign = false,
  locationId = null,
}) {
  const { formatMessage } = useLocalization();

  const jobId = jobInfo?.id;
  const jobNumber = jobInfo?.serviceJobNumber;

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, put, getApiUrl } = useDataProvider(provider);

  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatchJob = async () => {
    setLoading(true);
    let url = `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}/operations/dispatch/book-appointment`;
    let method = post;
    if (isRescheduleFlow) {
      url = `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}/operations/reschedule-appointment`;
      method = put;
    }
    const response = await method(url, {
      slotTypeCode: value?.slot?.code,
      requestDate: value?.textDate,
      recordVersion: jobInfo?.recordVersion,
      ...(isRescheduleFlow && { preferredServiceProviderId }),
    });
    if (response?.ok) {
      setSuccess(true);
      setLoading(false);
    } else {
      setErrors(response?.errors || []);
      setLoading(false);
      onClose();
    }
  };

  const assignJob = async () => {
    setLoading(true);
    const url = `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}/operations/reschedule-appointment`;
    const method = put;

    const response = await method(url, {
      slotTypeCode: value?.slot?.code,
      requestDate: value?.textDate,
      recordVersion: jobInfo?.recordVersion,
      ...(locationId && { locationId }),
      ...(preferredServiceProviderId && { preferredServiceProviderId }),
    });
    if (response?.ok) {
      setSuccess(true);
      setLoading(false);
    } else {
      setErrors(response?.errors || []);
      setLoading(false);
      onClose();
    }
  };

  const buttons = [
    ...(!success
      ? [
          {
            label: formatMessage(
              DispatchCalendarHelper.getMainActionButtonText({
                isReAssign,
                isAssign,
                isRescheduleFlow,
              }),
            ),
            onClick: () => {
              if (isAssign || isReAssign) {
                assignJob();
              } else {
                dispatchJob();
              }
            },
            variant: 'contained',
            color: 'primary',
            startIcon: <IconLibrary.Yes />,
          },
          {
            label: formatMessage({
              id: 'CANCEL',
              defaultMessage: 'Cancel',
            }),
            onClick: () => onClose(),
            variant: 'outlined',
            color: 'primary',
            startIcon: <IconLibrary.Cancel />,
          },
        ]
      : [
          {
            label: formatMessage({
              id: 'CLOSE',
              defaultMessage: 'Close',
            }),
            onClick: () => returnToJobPage(),
            variant: 'contained',
            color: 'primary',
            startIcon: <IconLibrary.Close />,
          },
        ]),
  ];

  const dialogTitleObject = DispatchCalendarHelper.getDialogTitle({
    isReAssign,
    isAssign,
    isRescheduleFlow,
  });

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          return;
        }
        onClose(null);
      }}
      maxWidth="sm"
      fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {DispatchCalendarHelper.getDialogIcon({
          isReAssign,
          isAssign,
          isRescheduleFlow,
        })}
        <FormattedMessage
          id={dialogTitleObject?.id}
          defaultMessage={dialogTitleObject?.defaultMessage}
        />
        &nbsp;&ndash;
        <Typography color="primary" variant="body">
          {jobNumber}
        </Typography>
        <DialogCloseButton
          onClose={() => {
            onClose(null);
          }}
        />
      </DialogTitle>
      <DialogContent>
        <AvailabilitySlotTypeProvider>
          <DispatchCalendarModalContent
            success={success}
            loading={loading}
            value={value}
            isRescheduleFlow={isRescheduleFlow}
            isReAssign={isReAssign}
            isAssign={isAssign}
          />
        </AvailabilitySlotTypeProvider>
      </DialogContent>

      <DialogActions display="flex" sx={{ justifyContent: 'center', my: 2 }}>
        {buttons.map(btn => {
          const { label, ...rest } = btn;
          return (
            <Button key={label} {...rest}>
              {label}
            </Button>
          );
        })}
      </DialogActions>
    </Dialog>
  );
}

export default DispatchCalendarModal;
